import {
  DisabledFieldReason,
  IReportDisabledFields
} from '../store/modules/report/types'

export const DISABLED_FIELD_MESSAGES: Record<DisabledFieldReason, string> = {
  FUTURE_RANGE:
    'A projeção não está disponível para datas futuras.',
  MULTI_MONTH_RANGE:
    'Informações disponíveis apenas para datas do mesmo mês.',
  NO_SALES_RECORDS:
    'Não é possível calcular a projeção porque o período não possui vendas registradas.'
}

export const FUTURE_SALES_MESSAGE =
  'Os valores referentes às vendas não estão disponíveis para datas futuras.'

export const LAST_YEAR_UNAVAILABLE_MESSAGE =
  'A comparação com as vendas do ano anterior não está disponível.'

const REPORT_VALUE_DEPENDENCIES = {
  totalSold: ['total.sold'],
  totalSales: ['total.sales'],
  totalItems: ['total.items'],
  salesDay: ['total.sold'],
  goalPercent: ['total.sold', 'metric.acDayGoal'],
  mainGoalPercent: ['total.sold', 'metric.mainGoal'],
  sellerGoalPercent: ['total.sold', 'metric.goalValue'],
  averageTicket: ['total.sold', 'total.sales'],
  itemsPerSale: ['total.items', 'total.sales'],
  averagePrice: ['total.sold', 'total.items'],
  lastYearVariation: ['total.sold'],
  goalGraphPercent: ['total.sold', 'metric.acDayGoal'],
  dynamicGoalSales: ['dynamicGoal.sales'],
  dynamicGoalItems: ['dynamicGoal.items'],
  dynamicGoalTicket: ['dynamicGoal.sold', 'dynamicGoal.sales'],
  dynamicGoalItemsPerSale: ['dynamicGoal.items', 'dynamicGoal.sales'],
  dynamicGoalPrice: ['dynamicGoal.sold', 'dynamicGoal.items'],
  acDayGoal: ['metric.acDayGoal'],
  mainGoal: ['metric.mainGoal'],
  projectionSold: ['projection.sold'],
  projectionGoal: ['projection.goal'],
  projectionCommission: ['projection.commission'],
  commission: ['commission']
} as const

export type ReportValue = keyof typeof REPORT_VALUE_DEPENDENCIES

const getReasonAtPath = (
  disabledFields: IReportDisabledFields,
  path: string
): DisabledFieldReason | undefined => {
  const [group, field] = path.split('.')
  const value = disabledFields[group as keyof IReportDisabledFields]

  return field
    ? (value as Record<string, DisabledFieldReason> | undefined)?.[field]
    : value as DisabledFieldReason | undefined
}

export const getReportValueDisabledReason = (
  disabledFields: IReportDisabledFields,
  value: ReportValue
): DisabledFieldReason | undefined => {
  for (const path of REPORT_VALUE_DEPENDENCIES[value]) {
    const reason = getReasonAtPath(disabledFields, path)
    if (reason) return reason
  }
}

export const getSalesDataDisabledReason = (
  disabledFields: IReportDisabledFields
): DisabledFieldReason | undefined => disabledFields.total?.sold ||
  disabledFields.total?.sales || disabledFields.total?.items

export const getReportAvailability = (
  disabledFields: IReportDisabledFields
) => ({
  acDayGoalReason: disabledFields.metric?.acDayGoal,
  commissionReason: disabledFields.commission,
  dynamicGoalItemsReason: disabledFields.dynamicGoal?.items,
  dynamicGoalSalesReason: disabledFields.dynamicGoal?.sales,
  goalValueReason: disabledFields.metric?.goalValue,
  mainGoalReason: disabledFields.metric?.mainGoal,
  projectionCommissionReason: disabledFields.projection?.commission,
  projectionGoalReason: disabledFields.projection?.goal,
  projectionSoldReason: disabledFields.projection?.sold,
  salesDataReason: getSalesDataDisabledReason(disabledFields),
  totalItemsReason: disabledFields.total?.items,
  totalSalesReason: disabledFields.total?.sales,
  totalSoldReason: disabledFields.total?.sold
})

export const SALES_DISABLED_FIELD_TOOLTIP_ID = 'report-disabled-sales'

export const getAccessibleUnavailableProps = (message: string) => ({
  'aria-label': `Informação indisponível. ${message}`
})

export const getSalesDisabledFieldTooltipProps = (
  disabledFields: IReportDisabledFields
) => getSalesDataDisabledReason(disabledFields)
  ? {
      'data-tip': true,
      'data-for': SALES_DISABLED_FIELD_TOOLTIP_ID,
      ...getAccessibleUnavailableProps(FUTURE_SALES_MESSAGE)
    }
  : {}

export const getDisabledFieldMessage = (reason?: string): string => {
  if (!reason) return ''

  return DISABLED_FIELD_MESSAGES[reason as DisabledFieldReason] ||
    'Informação indisponível.'
}

export const isFieldDisabled = (reason?: string): boolean => Boolean(reason)

export const getReportExportValue = <T>(
  reason: string | undefined,
  value: T | (() => T)
): T | '-' => {
  if (isFieldDisabled(reason)) return '-'
  return typeof value === 'function' ? (value as () => T)() : value
}

export const getDisabledFieldReasons = (
  disabledFields: IReportDisabledFields
): DisabledFieldReason[] => Array.from(new Set([
  disabledFields.dynamicGoal?.items,
  disabledFields.dynamicGoal?.sales,
  disabledFields.dynamicGoal?.sold,
  disabledFields.metric?.acDayGoal,
  disabledFields.metric?.goalValue,
  disabledFields.metric?.mainGoal,
  disabledFields.projection?.commission,
  disabledFields.projection?.goal,
  disabledFields.projection?.sold,
  disabledFields.total?.items,
  disabledFields.total?.sales,
  disabledFields.total?.sold,
  disabledFields.commission
].filter(Boolean))) as DisabledFieldReason[]

export const getDisabledFieldTooltipId = (reason: string): string =>
  `report-disabled-field-${reason}`

export const getDisabledFieldTooltipProps = (reason?: string) => reason
  ? {
      'data-tip': true,
      'data-for': getDisabledFieldTooltipId(reason),
      ...getAccessibleUnavailableProps(getDisabledFieldMessage(reason))
    }
  : {}

export const isAggregatedReportSortDisabled = (
  property: string,
  disabledFields: IReportDisabledFields
): boolean => {
  const valueByProperty: Partial<Record<string, ReportValue>> = {
    sold: 'totalSold',
    sales: 'totalSales',
    conversionRate: 'totalSales',
    items: 'totalItems',
    'total.items': 'totalItems',
    salesDay: 'salesDay',
    lastYearVariation: 'lastYearVariation',
    'average.tm': 'averageTicket',
    'average.pv': 'itemsPerSale',
    'average.pm': 'averagePrice',
    goalPercent: 'goalPercent',
    mainGoalPercent: 'mainGoalPercent',
    acDayGoal: 'acDayGoal',
    mainGoal: 'mainGoal',
    projection: 'projectionSold'
  }
  const value = valueByProperty[property]

  return value
    ? Boolean(getReportValueDisabledReason(disabledFields, value))
    : false
}
